/*
  Eigene Verfügbarkeit als Monatskalender (Mitarbeitenden-Ansicht).

  Bearbeitet wird mit demselben Editor und denselben Speicherregeln wie in der
  Planungsansicht (AvailabilityEditor, PUT /availability/day, automatisches
  Speichern). Ob ein Eintrag eine Freigabe braucht oder ein Tag gesperrt ist,
  entscheidet der Server; die Antwort zwingt User ohnehin auf sich selbst.

  Auswahl wie in der Planungsansicht: Klick wählt einen Tag, Ziehen mit der
  Maus einen Zeitraum, Umschalt-Klick erweitert bis zum angeklickten Tag,
  Strg/Cmd-Klick nimmt einzelne Tage dazu oder heraus.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addMonths, format } from "date-fns";
import { de } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import useToast from "../../../components/useToast";
import AppCallout from "../../../design-system/components/AppCallout";
import AppCard from "../../../design-system/components/AppCard";
import AppCardBody from "../../../design-system/components/AppCardBody";
import { AppStatusChip } from "../../../design-system/components/AppChip";
import AppDialog from "../../../design-system/components/AppDialog";
import AppDialogBody from "../../../design-system/components/AppDialogBody";
import AppDialogHeader from "../../../design-system/components/AppDialogHeader";
import { IconTime } from "../../../design-system/icons";
import ApprovalRequestsTable from "../../approval_flow/ApprovalRequestsTable";
import AppPeriodNavigator from "../../../design-system/components/AppPeriodNavigator";
import AppToolbar from "../../../design-system/components/AppToolbar";
import AppWorkspace from "../../../design-system/components/AppWorkspace";
import AvailabilityEditor from "../../availability/AvailabilityEditor";
import {
  fetchAvailabilityCalendar,
  fetchOwnPendingApprovals,
  putAvailabilityDay,
} from "../../availability/availabilityApi";
import { draftFromDay, defaultDraft } from "../../availability/availabilityDrafts";
import { buildDaySaveRequests, buildMonthCells, dayDisplay, rangeDates } from "./calendarModel";

const AUTOSAVE_DELAY_MS = 550;
// Strg/Cmd-Klick: der Editor öffnet erst, wenn nicht gleich weiter geklickt wird.
const MODIFIER_OPEN_DELAY_MS = 350;
const DESKTOP_EDITOR_QUERY = "(min-width: 1200px)";

function matchesDesktop() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia(DESKTOP_EDITOR_QUERY).matches;
}

function DayCell({ day, selected, handlers, t }) {
  const display = dayDisplay(day);
  const statusLabels = {
    availability: t("availability.status.available"),
    absence: t("availability.status.absent"),
    holiday: t("availability.status.holiday"),
  };
  const label = display.status === "holiday" && display.holidayName && display.holidayName !== "Ferien"
    ? display.holidayName
    : statusLabels[display.status] || "";
  const classes = [
    "employee-availability__day",
    `employee-availability__day--${display.isPending ? "pending" : display.status}`,
    display.isDefault ? "is-default" : "",
    display.isLocked ? "is-locked" : "",
    selected ? "is-selected" : "",
  ].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={classes}
      disabled={display.isLocked}
      aria-pressed={selected}
      title={day.blocked_message || undefined}
      onPointerDown={(event) => handlers.pointerDown(event, day.date)}
      onPointerEnter={(event) => handlers.pointerEnter(event, day.date)}
      onPointerUp={() => handlers.pointerUp()}
      onClick={(event) => handlers.click(event, day.date)}
    >
      <span className="employee-availability__day-number">{day.day}</span>
      {label ? <span className="employee-availability__day-status">{label}</span> : null}
      {display.timeLabel ? <span className="employee-availability__day-time">{display.timeLabel}</span> : null}
      {display.isPending ? (
        <span className="employee-availability__day-pending">{t("availability.pending_approval")}</span>
      ) : null}
    </button>
  );
}

export default function EmployeeAvailabilityPage() {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const [monthDate, setMonthDate] = useState(() => new Date());
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Hervorgehoben (während Ziehen/Strg-Klick) und im Editor bearbeitet sind
  // getrennt: sonst würde eine neue Auswahl den alten Entwurf automatisch auf
  // die neu markierten Tage speichern.
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [anchorDate, setAnchorDate] = useState("");
  const [draft, setDraft] = useState(defaultDraft(""));
  const [saving, setSaving] = useState(false);
  const [isDesktopEditor, setIsDesktopEditor] = useState(matchesDesktop);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingOpen, setPendingOpen] = useState(false);

  const month = format(monthDate, "yyyy-MM");
  const requestIdRef = useRef(0);
  const autoSaveReadyRef = useRef(false);
  const lastSavedSignatureRef = useRef("");
  const autoSaveTimerRef = useRef(null);
  const modifierOpenTimerRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false, startDate: "", endDate: "", suppressClick: false });

  const loadCalendar = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    try {
      const response = await fetchAvailabilityCalendar(token, { month });
      if (requestId !== requestIdRef.current) return null;
      setCalendar(response.data);
      setLoadError(false);
      return response.data;
    } catch {
      if (requestId === requestIdRef.current) setLoadError(true);
      return null;
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [month, token]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // Eigene offene Anfragen - wie der Hinweis auf der Planungsseite, aber nur
  // die eigenen und ohne Entscheidung.
  const loadPendingApprovals = useCallback(async () => {
    try {
      const response = await fetchOwnPendingApprovals(token);
      setPendingRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
    } catch {
      setPendingRows([]);
    }
  }, [token]);

  useEffect(() => {
    loadPendingApprovals();
  }, [loadPendingApprovals]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(DESKTOP_EDITOR_QUERY);
    const update = () => setIsDesktopEditor(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", update);
    return () => mediaQuery.removeEventListener?.("change", update);
  }, []);

  const closeEditor = useCallback(() => {
    window.clearTimeout(autoSaveTimerRef.current);
    window.clearTimeout(modifierOpenTimerRef.current);
    autoSaveReadyRef.current = false;
    setHighlightedDates([]);
    setSelectedDates([]);
    setAnchorDate("");
    setDraft(defaultDraft(""));
  }, []);

  // Ein anderer Monat: die Auswahl gehört zum alten.
  useEffect(() => {
    closeEditor();
  }, [closeEditor, month]);

  const days = useMemo(() => calendar?.days || [], [calendar]);
  const cells = useMemo(() => buildMonthCells(days), [days]);
  const holidayTypes = calendar?.holiday_types || [];
  const editorOpen = selectedDates.length > 0;
  const firstSelectedDay = days.find((day) => day.date === selectedDates[0]) || null;

  useEffect(() => {
    const stopDragging = () => { dragRef.current.active = false; };
    window.addEventListener("pointerup", stopDragging);
    return () => {
      window.removeEventListener("pointerup", stopDragging);
      window.clearTimeout(modifierOpenTimerRef.current);
    };
  }, []);

  const editableDates = (dates) =>
    dates.filter((key) => days.some((day) => day.date === key && day.is_editable !== false));

  const openEditor = (dates) => {
    const nextDates = [...dates].sort();
    if (!nextDates.length) {
      closeEditor();
      return;
    }
    const day = days.find((item) => item.date === nextDates[0]);
    const nextDraft = draftFromDay(day, holidayTypes);
    // Wie in der Planungsansicht: Klick auf bestehende Ferien behält deren
    // Zeitraum, sonst gilt die Auswahl.
    const keepsHolidayRange = nextDates.length === 1 && nextDraft.entry_kind === "holiday" && Boolean(nextDraft.start_date);
    const initializedDraft = {
      ...nextDraft,
      entry_kind: nextDraft.entry_kind === "free_request" ? "absence" : nextDraft.entry_kind,
      start_date: keepsHolidayRange ? nextDraft.start_date : nextDates[0],
      end_date: keepsHolidayRange ? nextDraft.end_date : nextDates[nextDates.length - 1],
    };

    window.clearTimeout(autoSaveTimerRef.current);
    autoSaveReadyRef.current = false;
    lastSavedSignatureRef.current = JSON.stringify({ draft: initializedDraft, dates: nextDates });
    setHighlightedDates(nextDates);
    setSelectedDates(nextDates);
    setDraft(initializedDraft);
    window.setTimeout(() => { autoSaveReadyRef.current = true; }, 0);
  };

  const dayHandlers = {
    pointerDown: (event, dateKey) => {
      // Fehlende Tastenangabe (ältere Umgebungen ohne PointerEvent) = Haupttaste.
      if ((event.button ?? 0) !== 0 || event.shiftKey || event.metaKey || event.ctrlKey) return;
      window.clearTimeout(modifierOpenTimerRef.current);
      dragRef.current = { active: true, moved: false, startDate: dateKey, endDate: dateKey, suppressClick: false };
      setHighlightedDates([dateKey]);
      setAnchorDate(dateKey);
    },
    pointerEnter: (event, dateKey) => {
      const drag = dragRef.current;
      if (!drag.active || (event.buttons ?? 1) !== 1) return;
      const nextDates = editableDates(rangeDates(drag.startDate, dateKey));
      dragRef.current = { ...drag, endDate: dateKey, moved: nextDates.length > 1, suppressClick: nextDates.length > 1 };
      setHighlightedDates(nextDates);
    },
    pointerUp: () => {
      const drag = dragRef.current;
      dragRef.current = { ...drag, active: false };
      if (drag.moved) openEditor(editableDates(rangeDates(drag.startDate, drag.endDate)));
    },
    click: (event, dateKey) => {
      const drag = dragRef.current;
      if (drag.suppressClick) {
        dragRef.current = { ...drag, suppressClick: false };
        return;
      }
      if (event.shiftKey && anchorDate) {
        openEditor(editableDates(rangeDates(anchorDate, dateKey)));
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        setAnchorDate(dateKey);
        setHighlightedDates((current) => {
          const nextDates = current.includes(dateKey)
            ? current.filter((key) => key !== dateKey)
            : [...current, dateKey].sort();
          window.clearTimeout(modifierOpenTimerRef.current);
          modifierOpenTimerRef.current = window.setTimeout(() => openEditor(nextDates), MODIFIER_OPEN_DELAY_MS);
          return nextDates;
        });
        return;
      }
      setAnchorDate(dateKey);
      openEditor([dateKey]);
    },
  };

  const saveEntry = useCallback(async (draftToSave, datesToSave) => {
    const requests = buildDaySaveRequests(draftToSave, datesToSave);
    if (!requests.length) return;
    setSaving(true);
    try {
      for (const request of requests) {
        await putAvailabilityDay(token, request.params, request.payload);
      }
      success(t("availability.editor.saved"));
      await Promise.all([loadCalendar(), loadPendingApprovals()]);
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.entry_save_failed"));
    } finally {
      setSaving(false);
    }
  }, [error, loadCalendar, loadPendingApprovals, success, t, token]);

  // Automatisch speichern wie in der Planungsansicht: kurz nach der letzten
  // Änderung, und nur wenn sich wirklich etwas geändert hat.
  useEffect(() => {
    if (!editorOpen || !autoSaveReadyRef.current || !draft.entry_kind || saving) return undefined;
    const signature = JSON.stringify({ draft, dates: selectedDates });
    if (signature === lastSavedSignatureRef.current) return undefined;
    window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(() => {
      lastSavedSignatureRef.current = signature;
      saveEntry(draft, selectedDates);
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(autoSaveTimerRef.current);
  }, [draft, editorOpen, saveEntry, saving, selectedDates]);

  const selectedItems = selectedDates.map((dateKey) => {
    const date = new Date(`${dateKey}T00:00:00`);
    return {
      id: dateKey,
      label: format(date, "dd.MM.yyyy"),
      userId: "self",
      userName: calendar?.selected_user || "",
      dateKey,
      dateLabel: format(date, "EEE, dd.MM", { locale: de }),
    };
  });
  const removeSelectedItem = (ids) => {
    const removed = new Set(Array.isArray(ids) ? ids : [ids]);
    const nextDates = selectedDates.filter((dateKey) => !removed.has(dateKey));
    if (!nextDates.length) {
      closeEditor();
      return;
    }
    setHighlightedDates(nextDates);
    setSelectedDates(nextDates);
  };

  const weekdayLabels = t("availability.weekdays.short", { returnObjects: true });
  const editor = editorOpen ? (
    <AvailabilityEditor
      dateKey={selectedDates[0]}
      mode={calendar?.selected_mode || "absence"}
      draft={draft}
      setDraft={setDraft}
      dayData={firstSelectedDay}
      holidayTypes={holidayTypes}
      onClose={closeEditor}
      selectedItems={selectedItems}
      onRemoveSelected={removeSelectedItem}
      commentLabel={t("availability.editor.comment")}
    />
  ) : null;

  return (
    <AppWorkspace title={t("availability.title")} className="availability employee-availability" loading={loading && !calendar}>
      {loadError ? (
        <AppCallout
          variant="error"
          title={t("availability.load_failed")}
          description={t("availability.load_failed_description")}
        />
      ) : null}

      <div className="availability__content-layout">
        <div className="availability__main-column">
          <AppToolbar
            left={(
              <AppPeriodNavigator
                viewMode="month"
                referenceDate={monthDate}
                previousLabel={t("availability.previous_month")}
                nextLabel={t("availability.next_month")}
                onPreviousPeriod={() => setMonthDate((current) => addMonths(current, -1))}
                onNextPeriod={() => setMonthDate((current) => addMonths(current, 1))}
                onSelectDate={setMonthDate}
              />
            )}
            right={pendingRows.length ? (
              <button
                type="button"
                className="availability__pending-chip-button"
                onClick={() => setPendingOpen(true)}
              >
                <AppStatusChip status="warning" size="md" leadingIcon={<IconTime />}>
                  {t("availability.pending.count_label", { count: pendingRows.length })}
                </AppStatusChip>
              </button>
            ) : null}
          />
          <AppCard>
            <AppCardBody>
              <div className="employee-availability__grid" role="grid" aria-busy={loading}>
                {(Array.isArray(weekdayLabels) ? weekdayLabels : []).map((label) => (
                  <div key={label} className="employee-availability__weekday" role="columnheader">
                    {label}
                  </div>
                ))}
                {cells.map((day, index) => (day ? (
                  <DayCell
                    key={day.date}
                    day={day}
                    selected={highlightedDates.includes(day.date)}
                    handlers={dayHandlers}
                    t={t}
                  />
                ) : (
                  <div key={`blank-${index}`} className="employee-availability__blank" aria-hidden="true" />
                )))}
              </div>
            </AppCardBody>
          </AppCard>
        </div>
        {editorOpen && isDesktopEditor ? <aside className="availability__editor-pane">{editor}</aside> : null}
      </div>

      <AppDialog
        open={editorOpen && !isDesktopEditor}
        onClose={closeEditor}
        showCloseButton={false}
        size="sm"
        className="availability__mobile-editor"
      >
        <AppDialogBody inset="none">{editor}</AppDialogBody>
      </AppDialog>

      <AppDialog
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        size="xl"
        className="availability__pending-dialog"
      >
        <AppDialogHeader title={t("availability.pending.title")} />
        <AppDialogBody className="availability__pending-dialog-body">
          {/* Ohne onApprove/onReject: nur lesen. */}
          <ApprovalRequestsTable
            rows={pendingRows}
            emptyState={t("availability.pending.empty")}
            paneClassName="availability__pending-table"
            density="compact"
            showStatus={false}
          />
        </AppDialogBody>
      </AppDialog>
    </AppWorkspace>
  );
}
